'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SkipForward, Flag, Users, LogOut, HeartHandshake, Video, Phone, UserPlus, Check, X, MessageCircle, Paperclip } from 'lucide-react';
import { ConnectionStatus } from './ConnectionStatus';
import { MessageBubble } from './MessageBubble';
import { AnimatedMessageInput } from '@/components/ui/animated-ai-chat';
import { TypingIndicator } from './TypingIndicator';
import { ReportModal } from './ReportModal';
import { GuestSetupModal } from './GuestSetupModal';
import { RulesModal } from './RulesModal';
import { CallUI } from './CallUI';
import { Button } from '@/components/ui/Button';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';
import { useChat } from '@/hooks/useChat';
import { useCall } from '@/hooks/useCall';
import { useChatStore, GenderType } from '@/store/chatStore';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ChatWindowProps {
  initialGenderFilter?: 'FEMALE' | null;
  matchSeconds?: number;
}

// Gender badge config
const genderConfig = {
  MALE: { label: 'M', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  FEMALE: { label: 'F', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
  OTHER: { label: '?', color: 'bg-brand-surface-2 text-brand-gray border-white/10' },
};

// ── Countdown Ring ────────────────────────────────────────────────────────────
function CountdownRing({
  countdown,
  total,
  onClick,
  size = 'md',
}: {
  countdown: number;
  total: number;
  onClick: () => void;
  size?: 'sm' | 'md';
}) {
  const dim = size === 'sm' ? 56 : 72;
  const r = size === 'sm' ? 22 : 28;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, countdown) / total);

  return (
    <button
      onClick={onClick}
      className="relative flex items-center justify-center hover:opacity-80 transition-opacity"
      aria-label={`Connect now (auto in ${countdown}s)`}
      style={{ width: dim, height: dim }}
    >
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} className="-rotate-90 absolute inset-0">
        <circle cx={dim / 2} cy={dim / 2} r={r} fill="none" stroke="#ffffff0d" strokeWidth={size === 'sm' ? 3 : 4} />
        <circle
          cx={dim / 2}
          cy={dim / 2}
          r={r}
          fill="none"
          stroke="#C4D600"
          strokeWidth={size === 'sm' ? 3 : 4}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.95s linear' }}
        />
      </svg>
      <span className={`relative font-bold text-brand-white ${size === 'sm' ? 'text-base' : 'text-xl'}`}>
        {countdown}
      </span>
    </button>
  );
}

export function ChatWindow({ initialGenderFilter, matchSeconds = 0 }: ChatWindowProps) {
  const {
    inputValue,
    status,
    messages,
    strangerTyping,
    handleInputChange,
    handleSendMessage,
    handleKeyDown,
    handleReport,
    findStranger,
    skipStranger,
    leaveChat,
    socketRef,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
  } = useChat();

  const {
    callStatus,
    callType,
    incomingType,
    localVideoRef,
    remoteVideoRef,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
  } = useCall(socketRef, status);

  const {
    genderFilter,
    setGenderFilter,
    strangerGender,
    strangerDisplayName,
    strangerIsMember,
    friendRequestStatus,
    friendRequestFrom,
    guestDisplayName,
    myGender,
    setGuestDisplayName,
    setMyGender,
    guestSetupPendingGender,
    setGuestSetupPendingGender,
  } = useChatStore();

  const { isPremium, isAuthenticated } = useAuth();
  const { addToast } = useUIStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasAutoStarted = useRef(false);
  // Return-to-chat modal for non-members who left mid-session
  const [showReturnModal, setShowReturnModal] = useState(false);

  // Pending find action after guest setup
  const [pendingFind, setPendingFind] = useState<{ gender?: 'FEMALE' } | null>(null);
  const [showGuestSetup, setShowGuestSetup] = useState(false);
  // Turnstile token (auto-refreshed by the widget rendered in idle/disconnected state)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Queued find intent — fires as soon as a Turnstile token arrives
  const [pendingAutoSearch, setPendingAutoSearch] = useState<{ gender?: 'FEMALE' } | null>(null);
  // Confirmation modal for switching between general ↔ female-only filter
  const [pendingFilterSwitch, setPendingFilterSwitch] = useState<{ to: 'FEMALE' | null } | null>(null);
  // Auto-connect countdown after a chat ends (5 → 0 → auto-find)
  const [countdown, setCountdown] = useState<number | null>(null);
  const [autoConnectCancelled, setAutoConnectCancelled] = useState(false);
  const autoConnectCancelledRef = useRef(false);
  // Stable ref to handleFindStranger so countdown effect never goes stale
  const handleFindStrangerRef = useRef<(gender?: 'FEMALE') => void>(() => {});
  // Rules modal
  const [showRulesModal, setShowRulesModal] = useState(false);
  // Skip cooldown
  const [skipCooldownMs, setSkipCooldownMs] = useState<number | null>(null);
  // Can reconnect with last stranger (premium only, 2-min window)
  const [canReconnect, setCanReconnect] = useState(false);
  // Call unlock: calls locked for 2 min after match; countdown in seconds
  const [callSecondsLeft, setCallSecondsLeft] = useState<number | null>(null);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callUnlockAtRef = useRef<number | null>(null);
  // Call confirmation modal
  const [pendingCallType, setPendingCallType] = useState<'audio' | 'video' | null>(null);

  // Attachment flow
  const pendingOutgoingFileRef = useRef<File | null>(null);
  const [waitingAttachmentAcceptance, setWaitingAttachmentAcceptance] = useState(false);
  const [pendingIncomingAttachment, setPendingIncomingAttachment] = useState<{ filename: string; size: number; mimeType: string } | null>(null);

  // Set gender filter from props
  useEffect(() => {
    if (initialGenderFilter) setGenderFilter(initialGenderFilter);
  }, [initialGenderFilter, setGenderFilter]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, strangerTyping]);

  // Guest needs setup if they're not authenticated and haven't provided name+gender yet
  const guestNeedsSetup = !isAuthenticated && (!guestDisplayName || !myGender);

  // Auto-start searching on mount — but only after setup
  useEffect(() => {
    if (!hasAutoStarted.current) {
      hasAutoStarted.current = true;

      // Show rules modal if user hasn't accepted yet
      const rulesAccepted = typeof window !== 'undefined' && localStorage.getItem('sw-rules-accepted');
      if (!rulesAccepted) {
        setShowRulesModal(true);
        return;
      }

      const currentFilter = useChatStore.getState().genderFilter;
      const filterToUse = (initialGenderFilter || currentFilter) as 'FEMALE' | null;

      // On hard reload, always start a fresh session — clear any lingering flag.
      // sessionStorage survives reload but the backend room is already gone (socket disconnected).
      const isReload = typeof window !== 'undefined' &&
        (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';
      if (isReload && typeof window !== 'undefined') {
        sessionStorage.removeItem('sw-prev-chat');
      }

      // Non-member returning to chat after leaving mid-session → show choice modal.
      // Emit chat:leave first so the server cleans up any lingering room state
      // from the previous session (the module-level socket stays connected across
      // client-side navigations, so the server still thinks the user is in the room).
      if (!isAuthenticated && typeof window !== 'undefined' && sessionStorage.getItem('sw-prev-chat')) {
        leaveChat();
        setShowReturnModal(true);
        return;
      }

      if (guestNeedsSetup) {
        setShowGuestSetup(true);
        setPendingFind({ gender: filterToUse || undefined });
      } else if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
        // No Turnstile configured — fire immediately (backend also skips check)
        findStranger(filterToUse || undefined);
      } else {
        // Queue — fires once the Turnstile widget delivers its token
        setPendingAutoSearch({ gender: filterToUse || undefined });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire the queued auto-search as soon as a Turnstile token is ready
  useEffect(() => {
    if (pendingAutoSearch !== null && turnstileToken) {
      findStranger(pendingAutoSearch.gender, turnstileToken);
      setTurnstileToken(null);
      setPendingAutoSearch(null);
    }
  }, [turnstileToken, pendingAutoSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for sidebar-triggered gender filter change (e.g. "Chat with Female" sidebar button)
  useEffect(() => {
    if (guestSetupPendingGender === null) return;
    const pendingGender = guestSetupPendingGender === 'FEMALE' ? 'FEMALE' : undefined;
    setGuestSetupPendingGender(null);
    if (guestNeedsSetup) {
      // Guest hasn't set up yet — show setup modal first
      setPendingFind({ gender: pendingGender });
      setShowGuestSetup(true);
    } else {
      // Show confirmation modal for all filter switches from sidebar
      requestFilterSwitch(pendingGender === 'FEMALE' ? 'FEMALE' : null);
    }
  }, [guestSetupPendingGender]); // eslint-disable-line react-hooks/exhaustive-deps

  const isConnected = status === 'connected';
  const isSearching = status === 'searching';

  // Keep handleFindStrangerRef current every render (avoids stale closure in countdown effect)
  handleFindStrangerRef.current = (gender?: 'FEMALE') => handleFindStranger(gender);

  // Track non-member active session in sessionStorage so we can show the
  // "Continue or New Stranger" prompt if they navigate away and come back.
  useEffect(() => {
    if (!isAuthenticated && typeof window !== 'undefined') {
      if (status === 'connected' || status === 'searching') {
        sessionStorage.setItem('sw-prev-chat', '1');
      }
    }
  }, [status, isAuthenticated]);

  // Start countdown when status becomes 'disconnected'; reset when chat resumes
  useEffect(() => {
    if (status === 'disconnected') {
      if (!autoConnectCancelledRef.current) {
        setCountdown(5);
      }
    } else {
      setCountdown(null);
      autoConnectCancelledRef.current = false;
      setAutoConnectCancelled(false);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick countdown; auto-connect when it reaches 0
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      const currentFilter = useChatStore.getState().genderFilter;
      handleFindStrangerRef.current(currentFilter || undefined);
      setCountdown(null);
      return;
    }
    const timer = setTimeout(
      () => setCountdown((prev) => (prev !== null ? prev - 1 : null)),
      1000
    );
    return () => clearTimeout(timer);
  }, [countdown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Request a filter switch:
  // • connected/searching → show confirmation modal (interrupts active state)
  // • idle/disconnected   → switch directly, no modal needed
  const requestFilterSwitch = (to: 'FEMALE' | null) => {
    if (status === 'connected' || status === 'searching') {
      setPendingFilterSwitch({ to });
    } else {
      setGenderFilter(to);
      if (to === 'FEMALE') {
        handleFindStranger('FEMALE');
      } else {
        handleFindStranger(undefined);
      }
    }
  };

  const cancelAutoConnect = () => {
    autoConnectCancelledRef.current = true;
    setAutoConnectCancelled(true);
    setCountdown(null);
  };

  // Rules modal acceptance — trigger auto-start logic
  const handleRulesAccept = () => {
    localStorage.setItem('sw-rules-accepted', '1');
    setShowRulesModal(false);
    const currentFilter = useChatStore.getState().genderFilter;
    const filterToUse = (initialGenderFilter || currentFilter) as 'FEMALE' | null;
    if (guestNeedsSetup) {
      setShowGuestSetup(true);
      setPendingFind({ gender: filterToUse || undefined });
    } else if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      findStranger(filterToUse || undefined);
    } else {
      setPendingAutoSearch({ gender: filterToUse || undefined });
    }
  };

  // Listen for skip cooldown, reconnect, and attachment events
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const onSkipCooldown = ({ remainingMs }: { remainingMs: number }) => {
      setSkipCooldownMs(remainingMs);
      setTimeout(() => setSkipCooldownMs(null), remainingMs);
    };

    const onReconnectUnavailable = () => {
      setCanReconnect(false);
    };

    const onAttachmentRequestReceived = ({ filename, size, mimeType }: { filename: string; size: number; mimeType: string }) => {
      setPendingIncomingAttachment({ filename, size, mimeType });
    };

    const onAttachmentAccepted = () => {
      const file = pendingOutgoingFileRef.current;
      if (!file) return;
      setWaitingAttachmentAcceptance(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        if (!dataUrl) return;
        socket.emit('attachment:send', { data: dataUrl, mimeType: file.type, filename: file.name });
        useChatStore.getState().addMessage({
          id: `att-sent-${Date.now()}`,
          content: file.name,
          senderId: 'me',
          isMe: true,
          timestamp: Date.now(),
          type: 'ATTACHMENT',
          attachment: { dataUrl, mimeType: file.type, filename: file.name },
        });
        pendingOutgoingFileRef.current = null;
      };
      reader.readAsDataURL(file);
    };

    const onAttachmentDeclined = () => {
      pendingOutgoingFileRef.current = null;
      setWaitingAttachmentAcceptance(false);
      addToast({ message: 'Stranger declined your file request.', type: 'error' });
    };

    socket.on('skip:cooldown', onSkipCooldown);
    socket.on('reconnect:unavailable', onReconnectUnavailable);
    socket.on('attachment:request_received', onAttachmentRequestReceived);
    socket.on('attachment:accepted', onAttachmentAccepted);
    socket.on('attachment:declined', onAttachmentDeclined);
    return () => {
      socket.off('skip:cooldown', onSkipCooldown);
      socket.off('reconnect:unavailable', onReconnectUnavailable);
      socket.off('attachment:request_received', onAttachmentRequestReceived);
      socket.off('attachment:accepted', onAttachmentAccepted);
      socket.off('attachment:declined', onAttachmentDeclined);
    };
  }, [socketRef, addToast]);

  // Show reconnect option for 2 min after a chat ends (premium only)
  useEffect(() => {
    if (status === 'disconnected' && isPremium) {
      setCanReconnect(true);
      const t = setTimeout(() => setCanReconnect(false), 2 * 60 * 1000);
      return () => clearTimeout(t);
    } else {
      setCanReconnect(false);
    }
  }, [status, isPremium]);

  // Start 2-min call unlock countdown when a match begins
  useEffect(() => {
    if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; }

    if (status === 'connected') {
      const LOCK_MS = 120 * 1000;
      callUnlockAtRef.current = Date.now() + LOCK_MS;
      setCallSecondsLeft(120);

      callTimerRef.current = setInterval(() => {
        const remaining = Math.ceil((callUnlockAtRef.current! - Date.now()) / 1000);
        if (remaining <= 0) {
          clearInterval(callTimerRef.current!);
          callTimerRef.current = null;
          callUnlockAtRef.current = null;
          setCallSecondsLeft(null);
        } else {
          setCallSecondsLeft(remaining);
        }
      }, 500); // 500ms for smoother display
    } else {
      callUnlockAtRef.current = null;
      setCallSecondsLeft(null);
      setPendingCallType(null);
      // Clear attachment state when chat ends
      setPendingIncomingAttachment(null);
      setWaitingAttachmentAcceptance(false);
      pendingOutgoingFileRef.current = null;
    }

    return () => { if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; } };
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // File attachment request
  const handleFileSelect = (file: File) => {
    if (!isConnected) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
    if (!allowed.includes(file.type)) {
      addToast({ message: 'Only images (jpg/png/gif/webp) and videos (mp4/webm) are allowed.', type: 'error' });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      addToast({ message: 'File must be under 8 MB.', type: 'error' });
      return;
    }
    pendingOutgoingFileRef.current = file;
    setWaitingAttachmentAcceptance(true);
    socketRef.current?.emit('attachment:request', { filename: file.name, size: file.size, mimeType: file.type });
  };

  // Wrap leaveChat to also clear the return-prompt flag
  const handleLeaveChat = () => {
    if (typeof window !== 'undefined') sessionStorage.removeItem('sw-prev-chat');
    leaveChat();
  };

  // User confirmed the switch in the modal
  const confirmFilterSwitch = () => {
    if (!pendingFilterSwitch) return;
    const { to } = pendingFilterSwitch;
    setPendingFilterSwitch(null);
    setGenderFilter(to);
    // Must end the current chat before searching again.
    // skipStranger() ends the room on the backend and auto-requeues;
    // handleFindStranger then dequeues and re-enqueues with the new filter.
    if (status === 'connected') {
      skipStranger();
    }
    if (to === 'FEMALE') {
      handleFindStranger('FEMALE');
    } else {
      handleFindStranger(undefined);
    }
  };

  // Called when guest finishes setup (receives Turnstile token from modal)
  const handleGuestSetupConfirm = (name: string, gender: GenderType, token: string | null) => {
    setGuestDisplayName(name);
    setMyGender(gender);
    setShowGuestSetup(false);
    if (pendingFind !== null) {
      findStranger(pendingFind.gender, token ?? undefined);
      setPendingFind(null);
    }
  };

  // Wrapped findStranger — waits for Turnstile token if not yet available
  const handleFindStranger = (gender?: 'FEMALE') => {
    if (guestNeedsSetup) {
      setPendingFind({ gender });
      setShowGuestSetup(true);
      return;
    }
    if (turnstileToken) {
      findStranger(gender, turnstileToken);
      setTurnstileToken(null);
    } else if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      findStranger(gender);
    } else {
      // Token not ready — queue; widget will deliver it shortly
      setPendingAutoSearch({ gender });
    }
  };

  // Unused variable removed — strangerDisplayName used directly in JSX

  return (
    <>
      {/* Turnstile widget — hidden, pre-fetches token for next find:stranger.
          Renders whenever we may need a token (idle/disconnected, no guest setup showing). */}
      {(status === 'idle' || status === 'disconnected') && !showGuestSetup && (
        <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
          <TurnstileWidget
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
          />
        </div>
      )}

      <div className="bc-main flex flex-col h-full">
        {/* ── Chat Header — editorial "Broadsheet at 3am" ─────────────────── */}
        <div className="bc-header shrink-0">
          <div className="bc-header-left">
            <div className="bc-eyebrow">
              <span className="num">§01</span>
              <span>
                {isSearching ? 'Listening' : isConnected ? 'Open line' : status === 'disconnected' ? 'Line ended' : 'Standby'}
              </span>
              {isConnected && strangerGender && (
                <span className={`text-xs px-1.5 py-0.5 rounded border font-bold ${genderConfig[strangerGender].color}`}>
                  {genderConfig[strangerGender].label}
                </span>
              )}
            </div>
            <div className="bc-title">
              {isConnected ? (
                <>
                  <span>Stranger</span>
                  <em>·</em>
                  <span className="name">{strangerDisplayName || 'anonymous'}</span>
                </>
              ) : isSearching ? (
                <>
                  <em>Listening for</em>
                  <span>a stranger</span>
                </>
              ) : status === 'disconnected' ? (
                <>
                  <span>Line</span>
                  <em>went</em>
                  <span>cold</span>
                </>
              ) : (
                <>
                  <span>Open</span>
                  <em>a</em>
                  <span>line</span>
                </>
              )}
            </div>
            <div className="bc-meta">
              <span>
                <span className={`bc-live-dot${isConnected ? '' : ' off'}`} />
                {isConnected ? 'CONNECTED' : isSearching ? 'SEARCHING' : 'IDLE'}
              </span>
              {isConnected && (
                <span>
                  DURATION
                  <b className="tnum">
                    {String(Math.floor(matchSeconds / 60)).padStart(2, '0')}:
                    {String(matchSeconds % 60).padStart(2, '0')}
                  </b>
                </span>
              )}
              <ConnectionStatus status={status} genderFilter={genderFilter} />
            </div>
          </div>

          {/* Header right — counter + actions */}
          <div className="flex items-center gap-2">
            {/* Female filter badge — click to switch to any gender */}
            {genderFilter && (
              <button
                onClick={() => requestFilterSwitch(null)}
                className="text-xs px-2 py-1 rounded-full bg-pink-500/20 text-pink-400 border border-pink-500/30 hover:bg-pink-500/30 hover:text-pink-300 flex items-center gap-1 transition-colors cursor-pointer"
                title="Click to remove female filter and match any gender"
              >
                Female filter <X size={10} />
              </button>
            )}

            {/* Call buttons — locked for first 2 min after match */}
            {isConnected && callStatus === 'idle' && (() => {
              const locked = callSecondsLeft !== null;
              const mins = locked ? Math.floor(callSecondsLeft! / 60) : 0;
              const secs = locked ? callSecondsLeft! % 60 : 0;
              const countdownLabel = `${mins}:${String(secs).padStart(2, '0')}`;
              const lockTitle = `Calls unlock in ${countdownLabel}`;
              return (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => !locked && setPendingCallType('audio')}
                    disabled={locked}
                    className={locked ? 'text-brand-gray-muted cursor-not-allowed flex items-center gap-1' : 'text-brand-gray hover:text-brand-lime flex items-center gap-1'}
                    title={locked ? lockTitle : 'Voice call'}
                  >
                    <Phone size={14} />
                    <span className="text-xs hidden sm:inline">{locked ? countdownLabel : 'Voice'}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => !locked && setPendingCallType('video')}
                    disabled={locked}
                    className={locked ? 'text-brand-gray-muted cursor-not-allowed flex items-center gap-1' : 'text-brand-gray hover:text-brand-lime flex items-center gap-1'}
                    title={locked ? lockTitle : 'Video call'}
                  >
                    <Video size={14} />
                    <span className="text-xs hidden sm:inline">{locked ? countdownLabel : 'Video'}</span>
                  </Button>
                </>
              );
            })()}

            {isConnected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReport}
                className="text-brand-gray hover:text-brand-red"
                title="Report stranger"
              >
                <Flag size={14} />
              </Button>
            )}

            {/* Add Friend button */}
            {isConnected && friendRequestStatus === 'idle' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!isAuthenticated) {
                    window.location.href = '/login?redirect=/chat';
                    return;
                  }
                  if (!strangerIsMember) {
                    // Trigger the socket event — backend will emit friend:error
                    sendFriendRequest();
                    return;
                  }
                  sendFriendRequest();
                }}
                className="text-brand-gray hover:text-brand-lime"
                title="Add stranger as friend"
              >
                <UserPlus size={14} />
              </Button>
            )}
            {isConnected && friendRequestStatus === 'sent' && (
              <span className="text-xs px-2 py-1 rounded-full bg-brand-lime/10 border border-brand-lime/20 text-brand-lime hidden sm:inline-flex">
                Request sent
              </span>
            )}
            {isConnected && friendRequestStatus === 'accepted' && (
              <span className="text-xs px-2 py-1 rounded-full bg-brand-lime/20 border border-brand-lime/30 text-brand-lime hidden sm:inline-flex items-center gap-1">
                <Check size={10} /> Friends
              </span>
            )}

            {(isConnected || isSearching) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={skipStranger}
                disabled={!!skipCooldownMs}
                title={skipCooldownMs ? `Cooldown: wait ${Math.ceil(skipCooldownMs / 1000)}s` : 'Skip to next stranger'}
              >
                <SkipForward size={14} />
                {skipCooldownMs ? `${Math.ceil(skipCooldownMs / 1000)}s` : 'Next'}
              </Button>
            )}

            {(status === 'idle' || status === 'disconnected') && (
              <Button size="sm" onClick={() => handleFindStranger(genderFilter || undefined)}>
                Find Stranger
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLeaveChat}
              className="text-brand-gray hover:text-brand-red"
              title="Leave chat"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>

        {/* ── Messages Area ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth">
          {/* Empty idle state */}
          {messages.length === 0 && status === 'idle' && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-brand-lime/10 flex items-center justify-center">
                <Users size={28} className="text-brand-lime" />
              </div>
              <div>
                <p className="text-brand-white font-semibold">Ready to chat?</p>
                <p className="text-sm text-brand-gray mt-1">Click Find Stranger to connect with someone new</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => handleFindStranger()}>
                  Find Stranger
                </Button>
                <Button
                  variant="outline"
                  onClick={() => requestFilterSwitch('FEMALE')}
                  className="border-pink-500/40 text-pink-400 hover:bg-pink-500/10 hover:border-pink-500/60"
                >
                  <HeartHandshake size={14} />
                  Chat with Female
                </Button>
              </div>
            </div>
          )}

          {/* Searching state overlay */}
          {messages.length === 0 && status === 'searching' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-16 h-16 rounded-full border-2 border-brand-lime border-t-transparent animate-spin" />
              <div className="text-center">
                <p className="text-brand-white font-medium">Searching...</p>
                <p className="text-sm text-brand-gray mt-1">
                  {genderFilter === 'FEMALE' ? 'Looking for a female stranger' : 'Finding you a stranger'}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLeaveChat}>
                Cancel
              </Button>
            </div>
          )}

          {/* Disconnected — full-screen countdown when no prior messages */}
          {status === 'disconnected' && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <p className="text-xs text-brand-gray uppercase tracking-widest">Chat ended</p>
              {!autoConnectCancelled ? (
                <>
                  <CountdownRing countdown={countdown ?? 0} total={5} onClick={() => { setCountdown(null); handleFindStranger(genderFilter || undefined); }} />
                  <p className="text-sm text-brand-gray">
                    Auto-connecting in <span className="text-brand-white font-medium">{countdown}s</span> — or tap to connect now
                  </p>
                  <Button variant="ghost" size="sm" onClick={cancelAutoConnect}>Cancel</Button>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-brand-surface-2 border border-white/10 flex items-center justify-center">
                    <Users size={24} className="text-brand-gray-light" />
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                    <Button onClick={() => handleFindStranger(genderFilter || undefined)}>Find Stranger</Button>
                    {canReconnect && (
                      <Button
                        variant="outline"
                        onClick={() => socketRef.current?.emit('reconnect:last')}
                        className="border-brand-lime/40 text-brand-lime hover:bg-brand-lime/10"
                      >
                        Reconnect
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => requestFilterSwitch('FEMALE')} className="border-pink-500/40 text-pink-400 hover:bg-pink-500/10">
                      <HeartHandshake size={14} /> Chat with Female
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Friend request received banner */}
          {isConnected && friendRequestStatus === 'received' && friendRequestFrom && (
            <div className="sticky top-2 z-10 mx-2 p-3 rounded-2xl bg-brand-surface border border-brand-lime/30 flex items-center justify-between gap-3 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-brand-lime/20 flex items-center justify-center shrink-0">
                  <UserPlus size={13} className="text-brand-lime" />
                </div>
                <p className="text-xs text-brand-white">
                  <span className="font-semibold">{friendRequestFrom.name}</span> wants to add you as a friend
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={acceptFriendRequest}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-brand-lime text-brand-black text-xs font-semibold hover:bg-brand-lime/80 transition-colors"
                >
                  <Check size={11} /> Accept
                </button>
                <button
                  onClick={rejectFriendRequest}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-brand-surface-2 text-brand-gray text-xs hover:text-brand-red transition-colors"
                >
                  <X size={11} /> Decline
                </button>
              </div>
            </div>
          )}

          {/* Incoming attachment request banner */}
          {isConnected && pendingIncomingAttachment && (
            <div className="sticky top-2 z-10 mx-2 p-3 rounded-2xl bg-brand-surface border border-brand-lime/30 flex items-center justify-between gap-3 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-brand-lime/20 flex items-center justify-center shrink-0">
                  <Paperclip size={13} className="text-brand-lime" />
                </div>
                <div>
                  <p className="text-xs text-brand-white font-semibold">Attachment request</p>
                  <p className="text-xs text-brand-gray truncate max-w-[160px]">
                    {pendingIncomingAttachment.filename} &middot; {formatBytes(pendingIncomingAttachment.size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    socketRef.current?.emit('attachment:accept');
                    setPendingIncomingAttachment(null);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-brand-lime text-brand-black text-xs font-semibold hover:bg-brand-lime/80 transition-colors"
                >
                  <Check size={11} /> Accept
                </button>
                <button
                  onClick={() => {
                    socketRef.current?.emit('attachment:decline');
                    setPendingIncomingAttachment(null);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-brand-surface-2 text-brand-gray text-xs hover:text-brand-red transition-colors"
                >
                  <X size={11} /> Decline
                </button>
              </div>
            </div>
          )}

          {/* Waiting for stranger to accept outgoing file */}
          {isConnected && waitingAttachmentAcceptance && (
            <div className="sticky top-2 z-10 mx-2 p-3 rounded-2xl bg-brand-surface border border-white/10 flex items-center gap-3 shadow-lg">
              <div className="w-4 h-4 border-2 border-brand-lime border-t-transparent rounded-full animate-spin shrink-0" />
              <p className="text-xs text-brand-gray flex-1">Waiting for stranger to accept your file&hellip;</p>
              <button
                onClick={() => {
                  socketRef.current?.emit('attachment:decline');
                  pendingOutgoingFileRef.current = null;
                  setWaitingAttachmentAcceptance(false);
                }}
                className="text-brand-gray hover:text-brand-red transition-colors"
                aria-label="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}

          {/* Typing indicator */}
          {strangerTyping && <TypingIndicator />}

          {/* Compact countdown/reconnect card — shown below prior messages after disconnect */}
          {status === 'disconnected' && messages.length > 0 && (
            <div className="flex flex-col items-center gap-3 py-6 border-t border-white/[0.04] mt-2 text-center">
              <p className="text-xs text-brand-gray uppercase tracking-widest">Chat ended</p>
              {!autoConnectCancelled ? (
                <>
                  <CountdownRing countdown={countdown ?? 0} total={5} onClick={() => { setCountdown(null); handleFindStranger(genderFilter || undefined); }} size="sm" />
                  <p className="text-sm text-brand-gray">
                    Auto-connecting in <span className="text-brand-white font-medium">{countdown}s</span>
                  </p>
                  <Button variant="ghost" size="sm" onClick={cancelAutoConnect}>Cancel</Button>
                </>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap justify-center">
                  <Button onClick={() => handleFindStranger(genderFilter || undefined)}>Find Stranger</Button>
                  {canReconnect && (
                    <Button
                      variant="outline"
                      onClick={() => socketRef.current?.emit('reconnect:last')}
                      className="border-brand-lime/40 text-brand-lime hover:bg-brand-lime/10"
                    >
                      Reconnect
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => requestFilterSwitch('FEMALE')} className="border-pink-500/40 text-pink-400 hover:bg-pink-500/10">
                    <HeartHandshake size={14} /> Chat with Female
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Message Input ─────────────────────────────────────────────────── */}
        <AnimatedMessageInput
          value={inputValue}
          onChange={handleInputChange}
          onSend={handleSendMessage}
          onKeyDown={handleKeyDown}
          status={status}
          onFileSelect={handleFileSelect}
        />
      </div>

      {/* Rules Modal — shown once per browser before first chat */}
      {showRulesModal && <RulesModal onAccept={handleRulesAccept} />}

      {/* Report Modal */}
      <ReportModal />

      {/* Guest Setup Modal */}
      {showGuestSetup && (
        <GuestSetupModal onConfirm={handleGuestSetupConfirm} />
      )}

      {/* Audio / Video Call UI */}
      <CallUI
        callStatus={callStatus}
        callType={callType}
        incomingType={incomingType}
        localVideoRef={localVideoRef}
        remoteVideoRef={remoteVideoRef}
        onAccept={acceptCall}
        onReject={rejectCall}
        onEnd={endCall}
      />

      {/* Return-to-chat modal — shown to non-members who left mid-session */}
      {showReturnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-brand-surface border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            {/* Icon */}
            <div className="w-12 h-12 rounded-full bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={22} className="text-brand-lime" />
            </div>

            {/* Title */}
            <h2 className="text-base font-semibold text-brand-white text-center mb-1">
              Welcome back!
            </h2>
            <p className="text-sm text-brand-gray text-center mb-6 leading-relaxed">
              You were chatting earlier. Would you like to continue with the same settings or connect with someone new?
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  sessionStorage.removeItem('sw-prev-chat');
                  setShowReturnModal(false);
                  const currentFilter = useChatStore.getState().genderFilter;
                  handleFindStranger(currentFilter || undefined);
                }}
                className="w-full h-11 rounded-xl bg-brand-lime text-brand-black text-sm font-semibold hover:bg-brand-lime/90 transition-colors"
              >
                Continue Chatting
              </button>
              <button
                onClick={() => {
                  sessionStorage.removeItem('sw-prev-chat');
                  setShowReturnModal(false);
                  setGenderFilter(null);
                  handleFindStranger(undefined);
                }}
                className="w-full h-11 rounded-xl border border-white/10 bg-brand-surface-2 text-sm text-brand-gray-light hover:text-brand-white hover:border-white/20 transition-colors"
              >
                Connect with a New Stranger
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call confirmation modal */}
      {pendingCallType !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-brand-surface border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center mx-auto mb-4">
              {pendingCallType === 'video' ? <Video size={22} className="text-brand-lime" /> : <Phone size={22} className="text-brand-lime" />}
            </div>
            <h2 className="text-base font-semibold text-brand-white text-center mb-1">
              Start {pendingCallType === 'video' ? 'video' : 'voice'} call?
            </h2>
            <p className="text-sm text-brand-gray text-center mb-6 leading-relaxed">
              Your browser will ask for {pendingCallType === 'video' ? 'camera and microphone' : 'microphone'} access. The stranger will need to accept.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingCallType(null)}
                className="flex-1 h-10 rounded-xl border border-white/10 bg-brand-surface-2 text-sm text-brand-gray-light hover:text-brand-white hover:border-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { startCall(pendingCallType); setPendingCallType(null); }}
                className="flex-1 h-10 rounded-xl bg-brand-lime text-brand-black text-sm font-semibold hover:bg-brand-lime/90 transition-colors"
              >
                {pendingCallType === 'video' ? 'Start Video' : 'Start Voice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter switch confirmation modal */}
      {pendingFilterSwitch !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-brand-surface border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            {/* Icon */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
              pendingFilterSwitch.to === 'FEMALE'
                ? 'bg-pink-500/10 border border-pink-500/20'
                : 'bg-brand-surface-2 border border-white/10'
            }`}>
              {pendingFilterSwitch.to === 'FEMALE' ? (
                <HeartHandshake size={22} className="text-pink-400" />
              ) : (
                <Users size={22} className="text-brand-gray-light" />
              )}
            </div>

            {/* Title */}
            <h2 className="text-base font-semibold text-brand-white text-center mb-1">
              {pendingFilterSwitch.to === 'FEMALE'
                ? 'Switch to female-only chat?'
                : 'Switch to general chat?'}
            </h2>

            {/* Description */}
            <p className="text-sm text-brand-gray text-center mb-6 leading-relaxed">
              {pendingFilterSwitch.to === 'FEMALE'
                ? 'You\'ll be matched only with female strangers. Your current chat will end.'
                : 'You\'ll be matched with anyone. Your current chat will end.'}
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setPendingFilterSwitch(null)}
                className="flex-1 h-10 rounded-xl border border-white/10 bg-brand-surface-2 text-sm text-brand-gray-light hover:text-brand-white hover:border-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmFilterSwitch}
                className={`flex-1 h-10 rounded-xl text-sm font-medium transition-colors ${
                  pendingFilterSwitch.to === 'FEMALE'
                    ? 'bg-pink-500/80 hover:bg-pink-500 text-white border border-pink-500/50'
                    : 'bg-brand-lime/90 hover:bg-brand-lime text-brand-black border border-brand-lime/50'
                }`}
              >
                {pendingFilterSwitch.to === 'FEMALE' ? 'Switch to Female' : 'Switch to General'}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
